import React, { useEffect, useState } from 'react'
import { graphql } from 'gatsby'
import styled from 'styled-components'

import Layout from '../components/Layout/Layout'
import SlideDocument from '../components/Carousel/SlideDocument/SlideDocument'

import Hero from '../components/Hero/Hero'
import PageWithBannerPresentation from '../components/PageWithBannerPresentation/PageWithBannerPresentation'
import pxToRem from '../utils/pxToRem'
import ProductHighlight from '../components/ProductHighlight/ProductHighlight'
import { deviceMax } from '../theme/breakpoints'
import ClientsBanner from '../components/ClientsBanner/ClientsBanner'
import CKEditorContent from '../components/CKEditorContent/CKEditorContent'
import { MainContainer } from '../theme/mainContainer.styled'
import Seo from '../components/Seo/Seo'
import NotFoundPage from '../pages/404'
import KeyFigures from '../components/KeyFigures/KeyFigures'
import { GetFirstSentenceFromRichText } from '../utils/GetFirstSentenceFromRichText'
import Button from '../components/Button/Button'
import RoutingEnum from '../routes/appRoutes'
import { SUSTAINABILITY_NAME } from '../utils/gtmEvents'
import BlockMixMediaIframe from '../components/BlockMixMediaIframe/BlockMixMediaIframe'

const StyledBlockPresentation = styled(MainContainer)`
  max-width: ${pxToRem(1198)};
  display: flex;
  margin: ${pxToRem(10)} auto ${pxToRem(40)} auto;
  gap: ${pxToRem(10)};
  padding: 0;
  @media ${deviceMax.tablet} {
    align-items: center;
    flex-direction: column;
  }
`

const StyledTextBloc = styled(MainContainer)`
  align-items: center;
  display: flex;
  margin-bottom: ${pxToRem(40)};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  font-size: ${({ theme }) => theme.fonts.sizes.titleH3};
  line-height: ${({ theme }) => theme.fonts.lineHeight.titleH3};
  text-align: center;
  justify-content: center;
  @media ${deviceMax.tablet} {
    padding: 0 ${pxToRem(20)};
  }
`

const StyledContainer = styled.div`
  flex-direction: column;
  display: flex;
`

const StyledProductHighlight = styled(ProductHighlight)`
  margin: 0 auto;
  max-width: ${pxToRem(1198)};
  gap: ${pxToRem(20)};
`

const StyledBlockPresentationWithoutMedia = styled(MainContainer)`
  align-items: flex-end;
  flex-direction: column;
  display: flex;
  max-width: ${pxToRem(400)};
  padding: 0;
  margin: 0;
  height: 100%;
  border-radius: ${pxToRem(5)};
  vertical-align: flex-start;
`
const StyledBlackContainer = styled.div`
  margin-bottom: ${pxToRem(40)};
  background-color: ${({ theme }) => theme.colors.unique.black};
`

const StyledClientsBanner = styled(ClientsBanner)`
  margin-top: ${pxToRem(60)};
`

const StyledPrimaryCta = styled(Button)`
  justify-content: center;
  @media ${deviceMax.tablet} {
    width: ${pxToRem(386)};
  }
  @media ${deviceMax.mobileM} {
    margin: 0 ${pxToRem(10)};
    width: calc(100% - ${pxToRem(20)});
  }
`

const StyledMainContainer = styled(MainContainer)`
    margin-bottom: ${pxToRem(40)};

    @media ${deviceMax.tablet} {
        padding: 0 ${pxToRem(20)};
    }
`

const PageWithBanner = ({ data, location }) => {
  if (data?.contentfulTemplatePageWithBanner === null) {
    return <NotFoundPage location={location} />
  }

  const {
    backgroundColor,
    clients,
    clientsBlocTitle,
    highlightedItems,
    id,
    imageHeaderDesktop,
    imageHeaderMobile,
    keyFiguresBanner,
    media,
    mixMediaIframe,
    msciHighlightedItems,
    msciClients,
    pageTitle,
    presentationBlocTitle,
    presentationBlocTextHtml,
    presentationBlocCarrousel,
    presentationBlocDocuments,
    primaryCta,
    textBlocHtml,
    seoMetaTitle,
    seoMetaDescription,
  } = data.contentfulTemplatePageWithBanner

  const parentPageNameForGtm =
    RoutingEnum.sustainability === location.pathname && SUSTAINABILITY_NAME

  const [slidesDocument] = useState(
    SlideDocument(presentationBlocDocuments, 'vertical', parentPageNameForGtm)
  )
  const [metaDesc, setMetaDesc] = useState('')

  useEffect(() => {
    const metadesc = GetFirstSentenceFromRichText(
      presentationBlocTextHtml?.content
    )
    setMetaDesc(metadesc)
  }, [])

  return (
    <Layout>
      <Seo
        title={seoMetaTitle ? seoMetaTitle.seoMetaTitle : pageTitle}
        description={
          seoMetaDescription ? seoMetaDescription.seoMetaDescription : metaDesc
        }
        canonicalHref={location.pathname}
      />
      <Hero
        mediaDesktop={imageHeaderDesktop}
        mediaMobile={imageHeaderMobile}
        title={pageTitle}
        backgroundColor={backgroundColor}
        pathname={location.pathname}
      />
      <StyledContainer>
        <PageWithBannerPresentation
          key={id}
          documents={presentationBlocDocuments}
          text={presentationBlocTextHtml}
          title={presentationBlocTitle}
          cards={presentationBlocCarrousel}
          cta={primaryCta}
          media={media}
        />
        {mixMediaIframe &&
          <StyledMainContainer>
            <BlockMixMediaIframe media={mixMediaIframe} />
          </StyledMainContainer>}
        {keyFiguresBanner ? (
          <StyledBlackContainer>
            <KeyFigures keyFiguresBanner={keyFiguresBanner} isCentered={true} />
          </StyledBlackContainer>
        ) : (
          textBlocHtml &&
          textBlocHtml.content !== '' && (
            <StyledBlackContainer>
              <StyledTextBloc>
                <CKEditorContent html={textBlocHtml.content} />
              </StyledTextBloc>
            </StyledBlackContainer>
          )
        )}

        {keyFiguresBanner && textBlocHtml && textBlocHtml.content !== '' && (
          <StyledTextBloc>
            <CKEditorContent html={textBlocHtml.content} />
          </StyledTextBloc>
        )}
        {highlightedItems?.length > 0 && (
          <StyledProductHighlight
            highlightedItems={highlightedItems}
            isAboutUs={location.pathname === RoutingEnum.aboutUs}
            msci={msciHighlightedItems}
          />
        )}
        {clients && (
          <StyledClientsBanner
            title={
              clientsBlocTitle?.clientsBlocTitle &&
              clientsBlocTitle.clientsBlocTitle
            }
            clients={clients}
            msci={msciClients}
          />
        )}
      </StyledContainer>
    </Layout>
  )
}
export default PageWithBanner

export const pageWithBanner = graphql`
  query PageWithBannerQuery($contentful_id: String!) {
    contentfulTemplatePageWithBanner(contentful_id: { eq: $contentful_id }) {
      id
      backgroundColor
      pageTitle
      seoMetaTitle {
        seoMetaTitle
      }
      seoMetaDescription {
        seoMetaDescription
      }
      primaryCta {
        label
        url
      }
      media {
          ...mediaFragment
      }
      clients {
        url
        textOnHover {
          textOnHover
        }
        logo {
          name
          altText {
            altText
          }
          cloudinaryAsset {
            resource_type
            format
            url
            secure_url
            context {
              custom {
                alt
                caption
              }
            }
          }
          contentfulAsset {
            file {
              url
            }
            gatsbyImageData
          }
          youtubeUrl
          videoThumbnail {
            resource_type
            format
            url
            secure_url
          }
        }
      }
      imageHeaderDesktop {
          ...imageHeaderFragment
      }
      imageHeaderMobile {
          ...imageHeaderFragment
      }
      clientsBlocTitle {
        clientsBlocTitle
      }
      presentationBlocTitle
      presentationBlocTextHtml {
        content
      }
      presentationBlocDocuments {
          ...documentFragment
      }
      presentationBlocCarrousel {
        __typename
        ... on ContentfulTemplateArticle {
          id
          backgroundColor
          date
          title
          metaDescription: seoMetaDescription {
            seoMetaDescription
          }
          slug
          thumbnail {
            name
            altText {
              altText
            }
            cloudinaryAsset {
              resource_type
              format
              secure_url
            }
            contentfulAsset {
              gatsbyImageData
              url
              file {
                url
                contentType
              }
            }
          }
        }
        ... on ContentfulTemplatePageStory {
          id
          date
          backgroundColor
          title
          seoMetaDescription
          slug
          thumbnail {
            name
            altText {
              altText
            }
            cloudinaryAsset {
              resource_type
              format
              secure_url
            }
            contentfulAsset {
              gatsbyImageData
              url
              file {
                url
                contentType
              }
            }
          }
        }
      }
      keyFiguresBanner {
        id
        url
        text {
          text
        }
        keyFigure
        document {
            ...documentFragment
        }
      }
      textBlocHtml {
        content
      }
      highlightedItems {
        textAlignment
        buttonVariant
        title {
          title
        }
        image {
            ...mediaFragment
        }
        textHtml {
          content
        }
        document {
            ...documentFragment
        }
        cta {
          url
          label
          urlAppAuto
          urlAppAero
        }
      }
      msciClients
      msciHighlightedItems
        mixMediaIframe {
            ... on ContentfulContentMedia {
                sys {
                    contentType {
                        sys {
                            id
                        }
                    }
                }
                ...mediaFragment
            }
            ... on ContentfulContentIframe {
                sys {
                    contentType {
                        sys {
                            id
                        }
                    }
                }
                id
                iframeUrl
            }
        }
    }
  }
`
